import { ok } from 'neverthrow'
import { fromBase64, toBase64 } from './atoms/convertors'
import { Hashing } from './atoms/hashing'
import { Random } from './atoms/random'
import { Symmetric } from './atoms/symmetric'
import { getCurrentEncryptionVersion } from './end2end/login'
import { prehashPassword } from './password'
import { createRecoveryKey, createSystemRecoveryKey } from './recovery'
import {
  crypto_secretbox_KEYBYTES,
  crypto_secretbox_NONCEBYTES,
  DERIVED_KEY_LENGTH,
} from './utils/constants'
import { getSodium, mapErr } from './utils/general'

const nonceSeed = new Uint8Array([
  164, 147, 47, 251, 200, 251, 107, 176, 49, 64, 214, 216, 211, 82, 23, 100, 23, 50, 83, 156, 253,
  165, 195, 139, 153, 202, 65, 6, 216, 178, 183, 249,
])

const secretSeed = new Uint8Array([
  224, 189, 223, 166, 120, 222, 71, 46, 53, 118, 217, 26, 248, 214, 48, 5, 62, 81, 136, 117, 32,
  177, 53, 208, 162, 194, 221, 6, 232, 120, 177, 96,
])

const keypairSeed = new Uint8Array([
  114, 226, 201, 4, 80, 146, 117, 179, 36, 9, 78, 168, 172, 99, 33, 171, 149, 77, 175, 116, 31, 154,
  32, 131, 164, 183, 37, 165, 176, 235, 62, 95,
])

/**
 * FOR TESTING PURPOSES ONLY,
 * absolutely insecure
 */

const defaultPassword = 'FaceUp4444'

export const mockReportBody = async (moreInfo?: string, victimName?: string) => {
  const nonce = await generateDeterministicNonce()
  const parts = JSON.stringify({
    moreInfo: moreInfo ?? null,
    victimName: victimName ?? null,
  })

  const secret = await generateDeterministicSecret()

  const userEncryptedBody = await Symmetric.encrypt(parts, secret, nonce)
  if (userEncryptedBody.isErr()) {
    return mapErr(userEncryptedBody, 'Mock report body failed')
  }

  return ok({
    body: await toBase64(userEncryptedBody.value),
    body_nonce: await toBase64(nonce),
  })
}

export const mockUserKeys = async (
  APP_SPECIFIC_SALT: string,
  SYSTEM_PUBLIC_KEY: string,
  password = defaultPassword,
  shouldGenerateRecovery = true
) => {
  const { publicKey, privateKey } = await generateDeterministicKeypair()

  const salt = await Hashing.generateSalt()

  const prehashedPassword = await prehashPassword({
    password,
    salt: await toBase64(salt),
    version: getCurrentEncryptionVersion() as 1 | 2,
  })

  if (prehashedPassword.isErr()) {
    console.error('prehashedPassword:', prehashedPassword.error.message)
    return
  }

  const { passwordKey, passwordKeyPrehash } = prehashedPassword.value
  const passwordKeyAppPrehash = await Hashing.deriveHashKey(
    DERIVED_KEY_LENGTH,
    Hashing.toPassword(await fromBase64(passwordKeyPrehash)),
    Hashing.toSalt(await fromBase64(APP_SPECIFIC_SALT))
  )

  if (passwordKeyAppPrehash.isErr()) {
    console.error('passwordKeyAppPrehash:', passwordKeyAppPrehash.error.message)
    return
  }

  const passwordHash = await Hashing.hashForStorage(Hashing.toPassword(passwordKeyAppPrehash.value))
  if (passwordHash.isErr()) {
    console.error('passwordHash:', passwordHash.error.message)
    return
  }

  const nonce = await Symmetric.generateNonce()
  const userEncryptedPrivateKey = await Symmetric.encrypt(
    privateKey,
    Symmetric.toKey(await fromBase64(passwordKey)),
    nonce
  )

  if (userEncryptedPrivateKey.isErr()) {
    console.error('userEncryptedPrivateKey:', userEncryptedPrivateKey.error.message)
    return
  }

  const recoveryPayload = await createRecoveryKey(
    await toBase64(userEncryptedPrivateKey.value),
    await toBase64(nonce),
    passwordKey
  )

  if (recoveryPayload.isErr()) {
    console.error('recoveryPayload:', recoveryPayload.error.message)
    return
  }

  const systemRecoveryKey = await createSystemRecoveryKey(
    recoveryPayload.value.recoveryKeyPlain,
    SYSTEM_PUBLIC_KEY
  )

  if (systemRecoveryKey.isErr()) {
    console.error('systemRecoveryKey:', systemRecoveryKey)
    return
  }

  return {
    public_key: await toBase64(publicKey),
    private_key: await toBase64(userEncryptedPrivateKey.value),
    nonce: await toBase64(nonce),
    salt: await toBase64(salt),
    password: passwordHash.value,

    ...(shouldGenerateRecovery && {
      private_key_recovery: recoveryPayload.value.privateKeyRecovery ?? undefined,
      private_key_recovery_nonce: recoveryPayload.value.privateKeyRecoveryNonce ?? undefined,
      recovery_key_encrypted: recoveryPayload.value.recoveryKeyEncrypted ?? undefined,
      recovery_key_encrypted_nonce: recoveryPayload.value.recoveryKeyEncryptedNonce ?? undefined,
      recovery_key_system_encrypted: systemRecoveryKey.value ?? undefined,
    }),
  }
}

export const generateChatMessage = async (message: string) => {
  const secret = await generateDeterministicSecret()
  const nonce = await generateDeterministicNonce()

  const ciphertext = await Symmetric.encrypt(message, secret, nonce)

  if (ciphertext.isErr()) {
    return mapErr(ciphertext, 'Generate chat message failed')
  }

  return ok({
    ciphertext: await toBase64(ciphertext.value),
    nonce: await toBase64(nonce),
  })
}

const generateDeterministicKeypair = async () => {
  const sodium = await getSodium()
  const { publicKey, privateKey } = sodium.crypto_box_seed_keypair(keypairSeed)

  return { publicKey, privateKey }
}

const generateDeterministicNonce = async () =>
  Symmetric.toNonce(await Random.generateBytesDeterministic(crypto_secretbox_NONCEBYTES, nonceSeed))

const generateDeterministicSecret = async () =>
  Symmetric.toKey(await Random.generateBytesDeterministic(crypto_secretbox_KEYBYTES, secretSeed))
